import { Dialog } from "@dash/dialog";
import { Button, IconButton } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { AlertTile, NotificationsPermissions, NotificationTile } from "@dash/nav";
import { useTheme } from "@emotion/react";
import React, { useMemo, useRef, useState } from "react";
import { MdNotificationsNone, MdOutlineNotifications, MdOutlineSettings } from "react-icons/md";

import { AlertObject, NotificationDto } from "@megaron/notifications-contracts";

import { useNotifications } from "../NotificationsProvider";

type Props = {
  hideNotification?: boolean;
  closeDrawer?: () => void;
  variant?: "default" | "pale";
  iconColor?: string;
  dialogLeft?: string;
};

export const NotificationButton: React.FunctionComponent<Props> = (props) => {
  const variant = props.variant ?? "default";
  const { unreadNotifications, queryKey } = useNotifications();
  const theme = useTheme();
  const { isDesktop } = useDeviceType();

  const [isOpen, setIsOpen] = useState(false);
  const [notificationListOpen, setNotificationListOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const outsideClickIgnoreRefs = useMemo(() => [triggerRef], []);

  const notifications = unreadNotifications();

  const sortNotificationsByDate = (items: { notifications: NotificationDto[]; alerts: AlertObject[] }) => {
    return [...items.notifications, ...items.alerts].sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date();
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date();
      return dateB.getTime() - dateA.getTime();
    });
  };

  const onToggleNotifications = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    props.closeDrawer?.();
    setIsOpen(true);
  };

  const panelAccent = variant === "pale" ? props.iconColor ?? theme.colors.primary : theme.colors.primary;

  return (
    <>
      {!props.hideNotification && (
        <div
          ref={triggerRef}
          css={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {variant === "pale" ? (
            <IconButton
              css={{
                fontSize: "1.35rem",
                padding: "0.25rem",
                margin: 0,
                borderRadius: theme.smallBorderRadius,
                background: "transparent",
                color: props.iconColor ?? theme.colors.primary,
                ":hover": {
                  background: "rgba(0,0,0,0.04)",
                },
              }}
              onClick={onToggleNotifications}
            >
              <MdOutlineNotifications />
            </IconButton>
          ) : (
            <Button
              icon={<MdOutlineNotifications />}
              css={{
                background: "rgba(255,255,255,0.15)",
                width: "32px",
                height: "32px",
              }}
              onClick={onToggleNotifications}
            />
          )}
          {notifications.count > 0 && (
            <div
              css={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                minWidth: "15px",
                height: "15px",
                padding: "0 3px",
                backgroundColor: "white",
                borderRadius: "50%",
                boxShadow: "0px 4px 4px rgba(0,0,0,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: props.iconColor ?? theme.colors.primary,
                fontSize: "10px",
                fontWeight: 900,
              }}
            >
              {notifications.count}
            </div>
          )}
        </div>
      )}

      {isOpen && (
        <Dialog
          onClose={() => setIsOpen(false)}
          hideHeader
          hideOverlay={isDesktop}
          zIndex={1010}
          outsideClickIgnoreRefs={outsideClickIgnoreRefs}
          background="white"
          contentCss={{
            border: `1px solid ${theme.colors.skeleton}`,
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            ...(isDesktop && {
              position: "fixed",
              margin: 0,
              width: "400px",
              top: "8px",
              left: props.dialogLeft ?? "268px",
            }),
          }}
          placement={isDesktop ? "center" : "top"}
        >
          <div
            css={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              marginBottom: "12px",
              color: panelAccent,
            }}
          >
            <MdOutlineNotifications
              css={{ width: "1.5rem", height: "1.5rem", flexShrink: 0, fill: panelAccent }}
            />
            <span css={{ fontWeight: 700, fontSize: "1.25rem", flexShrink: 0 }}>Powiadomienia</span>
            <div css={{ height: "1px", width: "100%", backgroundColor: theme.colors.skeleton, border: "none" }} />
            <MdOutlineSettings
              css={{
                width: "1.25rem",
                height: "1.25rem",
                flexShrink: 0,
                fill: panelAccent,
                cursor: "pointer",
              }}
              onClick={() => setNotificationListOpen(true)}
            />
          </div>
          {notifications && notifications.count > 0 ? (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                maxHeight: "750px",
                overflowY: "auto",
              }}
            >
              {sortNotificationsByDate(notifications)
                .slice(0, 5)
                .map((item, i) => (
                  <div key={i}>
                    {"readAt" in item ? (
                      <NotificationTile
                        notification={item}
                        queryKey={queryKey}
                        onClickNotification={() => setIsOpen(false)}
                      />
                    ) : (
                      <AlertTile alert={item} />
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                borderRadius: theme.smallBorderRadius,
                background: theme.colors.skeleton,
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                gap: "8px",
                color: theme.colors.secondaryText,
                height: "104px",
                width: "100%",
              }}
            >
              <MdNotificationsNone css={{ fontSize: "40px" }} />
              <span css={{ fontSize: "14px" }}>Brak powiadomień</span>
            </div>
          )}
        </Dialog>
      )}
      {notificationListOpen && (
        <Dialog onClose={() => setNotificationListOpen(false)} header="Ustawienia powiadomień" css={{ width: "400px" }}>
          <NotificationsPermissions notificationsQueryKey={queryKey} />
        </Dialog>
      )}
    </>
  );
};
